// src/utils/AuthStateChecker.ts - Updated version without the 404 endpoint
import { API_BASE_URL } from '../config';

/**
 * Utility to check and log the full authentication state
 * Includes client storage and server verification
 */
export const AuthStateChecker = {
  /**
   * Check and log the complete authentication state
   * @param operation - The operation that triggered this check
   * @param includeServerCheck - Whether to verify with server (default: true)
   */
  async check(operation: string, includeServerCheck = true): Promise<object> {
    console.group(`🔐 Auth State Check [${operation}]`);
    const startTime = performance.now();

    try {
      // 1. Collect all local storage auth values
      const localStorageAuth = this.getLocalStorageState();
      console.log('📦 LocalStorage State:', localStorageAuth);

      // 2. Collect all session storage auth values
      const sessionStorageAuth = this.getSessionStorageState();
      console.log('🔄 SessionStorage State:', sessionStorageAuth);

      // 3. Check server-side state if requested and we have a token
      let serverState = { checked: false };
      if (includeServerCheck && localStorageAuth.authToken) {
        serverState = await this.checkServerState(localStorageAuth.authToken);
        console.log('🖥️ Server State:', serverState);
      }

      // 4. Create a comprehensive state report
      const completeState = {
        timestamp: new Date().toISOString(),
        operation,
        client: {
          localStorage: localStorageAuth,
          sessionStorage: sessionStorageAuth,
          isAuthenticated: localStorageAuth.tripperAuthenticated === 'true',
          hasAccount: localStorageAuth.tripperHasAccount === 'true',
          explicitlyLoggedOut: localStorageAuth.tripperLoggedOut === 'true',
        },
        server: serverState
      };

      // 5. Log a simplified summary
      console.log('📊 Auth Summary:', {
        operation,
        authenticated: completeState.client.isAuthenticated,
        hasAccount: completeState.client.hasAccount,
        email: localStorageAuth.tripperUserEmail || sessionStorageAuth.socialAuthEmail,
        name: localStorageAuth.tripperUserName,
        serverId: serverState.user?.id,
        serverConfirmed: serverState.checked ? serverState.isAuthenticated : 'not checked',
        tokenValid: serverState.checked ? !serverState.tokenError : 'not checked',
      });

      return completeState;
    } catch (error) {
      console.error('❌ Error during auth state check:', error);
      return { error: error instanceof Error ? error.message : 'Unknown error', operation };
    } finally {
      const duration = (performance.now() - startTime).toFixed(2);
      console.log(`⏱️ Auth check completed in ${duration}ms`);
      console.groupEnd();
    }
  },

  /**
   * Get all auth-related values from localStorage
   */
  getLocalStorageState(): Record<string, string | null> {
    const authKeys = [
      'tripperAuthenticated',
      'tripperHasAccount',
      'tripperUserEmail',
      'tripperUserName',
      'tripperUserId',
      'tripperSessionToken',
      'tripperCodeValidated',
      'tripperLoggedOut',
      'tripperUserExists',
      'tripperInviteSent',
      'tripperAccountJustCreated',
      'tripperCodeValidatedBeforeSocial',
      'authToken',
      'token',
      'userData',
      'rememberedEmail'
    ];

    const state: Record<string, string | null> = {};
    
    authKeys.forEach(key => {
      state[key] = localStorage.getItem(key);
    });

    return state;
  },

  /**
   * Get all auth-related values from sessionStorage
   */
  getSessionStorageState(): Record<string, string | null> {
    const authKeys = [
      'socialAuthSuccess',
      'socialAuthProvider',
      'socialAuthEmail',
      'socialAuthAttemptTime',
      'socialAuthAttemptEmail',
      'socialAuthHasExistingAccount',
      'socialAuthError',
      'tripperAuthError',
      'tripperAccountCreated',
      'tripperLogoutTime',
      'recentlyLoggedOut',
      'lastAttemptedEmail'
    ];

    const state: Record<string, string | null> = {};
    
    authKeys.forEach(key => {
      state[key] = sessionStorage.getItem(key);
    });

    return state;
  },

  /**
   * Check authentication state with the server
   */
  async checkServerState(token: string): Promise<any> {
    try {
      // Verify token validity
      const verifyResponse = await fetch(`${API_BASE_URL}/api/auth/verify`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        signal: AbortSignal.timeout(3000) // 3-second timeout
      });

      if (!verifyResponse.ok) {
        return {
          checked: true,
          isAuthenticated: false,
          tokenError: true,
          statusCode: verifyResponse.status,
          statusText: verifyResponse.statusText
        };
      }

      const verifyData = await verifyResponse.json();
      
      // If token is valid, return user profile details
      if (verifyData.success && verifyData.user) {
        return {
          checked: true,
          isAuthenticated: true,
          tokenError: false,
          user: verifyData.user,
          userDetails: verifyData.userDetails || null
        };
      }

      return {
        checked: true,
        isAuthenticated: false,
        tokenError: false,
        message: verifyData.message || 'Unknown server response'
      };
    } catch (error) {
      return {
        checked: true,
        isAuthenticated: false,
        tokenError: true,
        error: error instanceof Error ? error.message : 'Unknown error during server check'
      };
    }
  }
};